import {
  canonicalGuaranteedMode,
  canonicalGuaranteedOverride,
  isGuaranteedMode,
  type GuaranteedMode,
  type GuaranteedServiceOrgSkuEntry,
  type GuaranteedServiceSkuEntry,
} from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import type { LampTone } from '../../ui/index.ts';

export const MODE_VALUES = ['off', 'shadow', 'act'] as const;

/** Off is a dead lamp, shadow amber, remove / on the accent — the same on every mechanism. */
export function modeLamp(mode: GuaranteedMode): LampTone {
  switch (mode) {
    case 'off':
      return 'off';
    case 'shadow':
      return 'warn';
    case 'act':
      return 'signal';
  }
}

export function modeLabelKey(mode: GuaranteedMode): I18nKey {
  switch (mode) {
    case 'off':
      return 'guaranteed.mode.off';
    case 'shadow':
      return 'guaranteed.mode.shadow';
    case 'act':
      return 'guaranteed.mode.act';
  }
}

/**
 * Ultimate fallback is not a trip mechanism: `act` walks every leg on a
 * non-2xx and removes nothing, so it must not read 「摘除」 (#2658).
 */
export function fallbackLabelKey(mode: GuaranteedMode): I18nKey {
  switch (mode) {
    case 'off':
      return 'guaranteed.fallback.off';
    case 'shadow':
      return 'guaranteed.fallback.shadow';
    case 'act':
      return 'guaranteed.fallback.act';
  }
}

export function skuProbeMode(row: GuaranteedServiceSkuEntry): GuaranteedMode {
  return canonicalGuaranteedMode(row.probe_mode) ?? (row.probe_enabled ? 'act' : 'off');
}

export function skuChainMode(row: GuaranteedServiceSkuEntry): GuaranteedMode {
  return canonicalGuaranteedMode(row.chain_trip_mode) ?? (row.chain_trip ? 'act' : 'off');
}

export function skuFallbackMode(row: GuaranteedServiceSkuEntry): GuaranteedMode {
  return canonicalGuaranteedMode(row.ultimate_fallback) ?? 'off';
}

export function overlayFallback(row: GuaranteedServiceOrgSkuEntry): 'inherit' | GuaranteedMode {
  return canonicalGuaranteedOverride(row.ultimate_fallback) ?? 'inherit';
}

export function isMode(value: string): value is GuaranteedMode {
  return isGuaranteedMode(value);
}
